'use client';
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

export type UserRole = 'super_admin' | 'company_admin' | 'client' | 'mechanic';

export interface AuthUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  companyId?: string;
  companyName?: string;
}

interface AuthContextType {
  user: AuthUser | null;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; deactivated?: boolean }>;
  logout: () => void;
}

const AUTH_KEY = 'jobcardflow_auth_user';
const SUBSCRIBERS_KEY = 'jobcardflow_subscriber_companies';

// Demo users for all 4 roles
export const DEMO_USERS: Array<AuthUser & { password: string; subscriberId?: string }> = [
  {
    id: 'super-001',
    name: 'Riaan Botha',
    email: 'riaan@jobcardflow.co.za',
    password: 'Admin@2026!',
    role: 'super_admin',
  },
  {
    id: 'cadmin-001',
    name: 'Danie Venter',
    email: 'admin@apexmech.co.za',
    password: 'CompAdmin@2026!',
    role: 'company_admin',
    companyId: 'sub-002',
    companyName: 'Apex Mechanical Services',
    subscriberId: 'sub-002',
  },
  {
    id: 'mech-001',
    name: 'Sipho Mthembu',
    email: 'sipho.m@jobcardflow.co.za',
    password: 'Mech@2026!',
    role: 'mechanic',
    companyId: 'sub-001',
    companyName: 'JobCardFlow Demo Co.',
    subscriberId: 'sub-001',
  },
  {
    id: 'client-001',
    name: 'Rand Water Procurement',
    email: 'procurement@randwater.co.za',
    password: 'Client@2026!',
    role: 'client',
  },
];

function getSubscriberActive(subscriberId: string): boolean {
  if (typeof window === 'undefined') return true;
  try {
    const stored = localStorage.getItem(SUBSCRIBERS_KEY);
    if (stored) {
      const subs = JSON.parse(stored);
      const sub = subs.find((s: { id: string; active: boolean }) => s.id === subscriberId);
      if (sub) return sub.active;
    }
  } catch { /* ignore */ }
  return true; // default to active if not found
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    try {
      const stored = localStorage.getItem(AUTH_KEY);
      if (stored) {
        setUser(JSON.parse(stored));
      }
    } catch {
      // ignore
    }
    setIsLoading(false);
  }, []);

  const login = async (email: string, password: string): Promise<{ success: boolean; error?: string; deactivated?: boolean }> => {
    await new Promise((r) => setTimeout(r, 800));
    const found = DEMO_USERS.find(
      (u) => u.email.toLowerCase() === email.toLowerCase() && u.password === password
    );
    if (!found) {
      return { success: false, error: 'Invalid email or password. Use the demo accounts below.' };
    }

    // Check if the subscriber company is active (for company_admin and mechanic roles)
    if (found.subscriberId) {
      const isActive = getSubscriberActive(found.subscriberId);
      if (!isActive) {
        return { success: false, deactivated: true, error: 'Your company account has been deactivated. Please make payment to reactivate.' };
      }
    }

    const { password: _pw, subscriberId: _sid, ...authUser } = found;
    setUser(authUser);
    localStorage.setItem(AUTH_KEY, JSON.stringify(authUser));
    return { success: true };
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem(AUTH_KEY);
  };

  return (
    <AuthContext.Provider value={{ user, isLoading, login, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
